import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X as XIcon } from "@phosphor-icons/react";
import { CONTACT_ROLES, CONTACT_POSTURES } from "@/components/ContactsPanel";

/**
 * Inline contact editor for the Contact Directory.
 *
 * Iter 85.100 · Lives inside a modal because every field it edits (name /
 * title / role / posture / influence / email / phone / linkedin / notes)
 * is covered by the existing PATCH /clients/{cid}/contacts/{contact_id}
 * whitelist. `owner_user_id` and `is_primary` are edited from the deeper
 * ContactHistoryDrawer to keep this dialog focused on core directory
 * hygiene.
 *
 * Iter 85.101 · `tags` are edited via a chip input with Enter/comma/Tab
 * committing and Backspace removing the last chip.
 *
 * Iter 85.102 · Tag autocomplete state — `suggestIdx = -1` means the
 * list is visible but nothing is highlighted (Enter commits the raw
 * draft). Down-arrow moves through matches; Esc closes the panel.
 *
 * Iter 86.6 · Extracted from ContactDirectory.jsx to keep that file
 * under the 700-line file guideline.
 */
export function ContactEditDialog({ row, allTags = [], onOpenChange, onSaved }) {
  const open = Boolean(row);
  const [form, setForm] = useState({ name: "", title: "", role: "other", posture: "unknown", influence: "medium", email: "", phone: "", linkedin_url: "", notes: "", tags: [] });
  const [tagDraft, setTagDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggestIdx, setSuggestIdx] = useState(-1);
  const [suggestOpen, setSuggestOpen] = useState(false);
  useEffect(() => {
    if (!row) return;
    setForm({
      name: row.name || "",
      title: row.title || "",
      role: row.role || "other",
      posture: row.posture || "unknown",
      influence: row.influence || "medium",
      email: row.email || "",
      phone: row.phone || "",
      linkedin_url: row.linkedin_url || "",
      notes: row.notes || "",
      tags: Array.isArray(row.tags) ? [...row.tags] : [],
    });
    setTagDraft("");
    setSuggestIdx(-1);
    setSuggestOpen(false);
  }, [row]);
  const addTag = (raw) => {
    const v = String(raw || "").trim().toLowerCase();
    if (!v) return;
    setForm((f) => (f.tags.includes(v) ? f : { ...f, tags: [...f.tags, v] }));
    setTagDraft("");
    setSuggestIdx(-1);
    setSuggestOpen(false);
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));
  // Filter the tag universe against the current draft. Substring (not
  // just prefix) so "invest" finds "investor" AND "reinvestor". Already-
  // owned tags are hidden so no redundant option ever appears. Capped
  // at 8 to keep the dropdown visually contained.
  const suggestions = useMemo(() => {
    const draft = tagDraft.trim().toLowerCase();
    if (!draft) return [];
    const owned = new Set(form.tags);
    return (allTags || [])
      .filter((t) => t && !owned.has(t) && t.toLowerCase().includes(draft))
      .slice(0, 8);
  }, [tagDraft, allTags, form.tags]);
  const onTagKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setSuggestOpen(true);
      setSuggestIdx((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setSuggestOpen(true);
      setSuggestIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      return;
    }
    if (e.key === "Escape") {
      if (suggestOpen) { e.preventDefault(); setSuggestOpen(false); setSuggestIdx(-1); }
      return;
    }
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      const picked = suggestOpen && suggestIdx >= 0 ? suggestions[suggestIdx] : null;
      if (picked) { e.preventDefault(); addTag(picked); return; }
      if (tagDraft.trim()) { e.preventDefault(); addTag(tagDraft); }
    } else if (e.key === "Backspace" && !tagDraft && form.tags.length) {
      setForm((f) => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };
  const save = async () => {
    if (!row || saving) return;
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    // Fold any un-committed tag draft into the payload so partners
    // don't lose a tag they typed but forgot to press Enter on.
    const tagsPayload = tagDraft.trim()
      ? [...form.tags, tagDraft.trim().toLowerCase()].filter((v, i, a) => a.indexOf(v) === i)
      : form.tags;
    setSaving(true);
    try {
      const { data } = await api.patch(`/clients/${row.client_id}/contacts/${row.id}`, { ...form, tags: tagsPayload });
      const saved = data?.contact || { ...form, tags: tagsPayload };
      onSaved({ ...row, ...saved });
      toast.success("Contact updated");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-2xl" data-testid="contact-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Edit contact</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {row?.client_name ? <>Partner org · {row.client_name}</> : "Update the details captured against this contact."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name">
            <Input data-testid="contact-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-secondary/40" />
          </Field>
          <Field label="Title">
            <Input data-testid="contact-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none bg-secondary/40" />
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="contact-edit-role" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTACT_ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Posture">
            <Select value={form.posture} onValueChange={(v) => setForm({ ...form, posture: v })}>
              <SelectTrigger data-testid="contact-edit-posture" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTACT_POSTURES.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Influence">
            <Select value={form.influence} onValueChange={(v) => setForm({ ...form, influence: v })}>
              <SelectTrigger data-testid="contact-edit-influence" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Email">
            <Input data-testid="contact-edit-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none bg-secondary/40" />
          </Field>
          <Field label="Phone">
            <Input data-testid="contact-edit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none bg-secondary/40" />
          </Field>
          <Field label="LinkedIn URL">
            <Input data-testid="contact-edit-linkedin" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} className="rounded-none bg-secondary/40" placeholder="https://linkedin.com/in/…" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Firm-only notes">
              <textarea
                data-testid="contact-edit-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full bg-secondary/40 border border-border rounded-none px-3 py-2 text-sm outline-none focus-visible:border-primary"
                placeholder="Partner-only intelligence about this contact. Never sent to the client."
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Tags">
              {/* Iter 85.101 · Chip editor. Enter / comma / Tab commits
                  the current draft; Backspace on an empty draft removes
                  the last chip. All values are lowercased server-side. */}
              <div
                className="w-full bg-secondary/40 border border-border rounded-none px-2 py-1.5 min-h-[2.25rem] flex flex-wrap items-center gap-1.5 focus-within:border-primary relative"
                data-testid="contact-edit-tags"
              >
                {form.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 text-[0.625rem] font-mono uppercase tracking-widest border border-brass/50 bg-brass-soft/30 text-brass px-1.5 py-0.5"
                    data-testid={`contact-edit-tag-chip-${t}`}
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      aria-label={`Remove ${t}`}
                      className="text-brass/70 hover:text-destructive"
                    >
                      <XIcon size={9} weight="bold" />
                    </button>
                  </span>
                ))}
                <input
                  data-testid="contact-edit-tag-input"
                  value={tagDraft}
                  onChange={(e) => {
                    setTagDraft(e.target.value);
                    setSuggestOpen(true);
                    setSuggestIdx(-1);
                  }}
                  onKeyDown={onTagKeyDown}
                  onFocus={() => setSuggestOpen(true)}
                  // 150ms delay so clicking a suggestion registers before
                  // blur nukes the dropdown. Un-committed text still folds
                  // into the payload on save.
                  onBlur={() => {
                    setTimeout(() => { setSuggestOpen(false); setSuggestIdx(-1); }, 150);
                  }}
                  placeholder={form.tags.length ? "" : "investor, docent, referral source…"}
                  className="flex-1 min-w-[8rem] bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground"
                />
                {suggestOpen && suggestions.length > 0 && (
                  <ul
                    className="absolute left-0 right-0 top-full mt-1 z-10 border border-border bg-popover shadow-md max-h-56 overflow-y-auto"
                    data-testid="contact-edit-tag-suggestions"
                  >
                    {suggestions.map((t, i) => {
                      const active = i === suggestIdx;
                      return (
                        <li key={t}>
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); addTag(t); }}
                            onMouseEnter={() => setSuggestIdx(i)}
                            data-testid={`contact-edit-tag-suggestion-${t}`}
                            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 border-l-2 transition-colors ${active ? "border-primary bg-secondary/60" : "border-transparent hover:bg-secondary/40"}`}
                          >
                            <span className="text-[0.625rem] font-mono uppercase tracking-widest text-brass">{t}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground/70 mt-1">
                {suggestOpen && suggestions.length > 0
                  ? "↑↓ to pick · Enter/Tab commits · Esc closes suggestions"
                  : "Press Enter or comma to add · Backspace removes the last chip"}
              </div>
            </Field>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-none"
            data-testid="contact-edit-cancel"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="rounded-none"
            data-testid="contact-edit-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

export default ContactEditDialog;
